import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FeatureLink } from '@common/navigation/feature-link';
import { featureLinks } from '@common/navigation/feature-links';
import { NavigationService } from '@common/navigation/navigation.service';
import { EMPTY, ReplaySubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AuthUser } from '../auth/models/auth-user.model';
import { AuthService } from '../auth/services/auth.service';
import { ShopifyLink, ShopifyMigrationService } from '../navigation/shopify-migration.service';
import { EditShopifyLinkModalComponent } from './edit-shopify-link-modal/edit-shopify-link-modal.component';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {
  featureLinks: FeatureLink[];
  subs = new SubSink();
  user: AuthUser;
  favorites$ = this.navigationService.getFavorites();
  filterQuery = '';

  private shopifyLinksSubject$ = new ReplaySubject<ShopifyLink[]>(1);
  shopifyLinks$ = this.shopifyLinksSubject$.asObservable();

  constructor(
    private navigationService: NavigationService,
    private authService: AuthService,
    private shopifyMigrationService: ShopifyMigrationService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.fetchShopifyLinks();
    this.featureLinks = this.navigationService.getFeatureLinks();
    this.subs.sink = this.authService.getUser().subscribe(user => {
      this.user = user;
    });
  }

  /**
   * Displays statistics about transferred apps to shopify.
   *
   * @param shopifyLinks
   * @param featureLink
   * @returns
   */
  getCoverage(shopifyLinks: ShopifyLink[]) {
    const percent = featureLinks.length === 0 ?
      0 : Math.round((shopifyLinks.length / featureLinks.length) * 100);

    return `Transferred to Shopify: ${shopifyLinks.length} / ${featureLinks.length} (${percent}%)`;
  }

  /**
   * If app is transferred to shopify and it has force redirect set to true,
   * route to shopify path.
   */
  getLinkPath(featureLink: FeatureLink) {
    return this.shopifyLinks$.pipe(
      map(links => links.find(link => link.path === featureLink.path)),
      map(link => {
        if (link && link.forceRedirect) {
          return link.shopifyLink;
        } else {
          return `/${featureLink.path}`;
        }
      })
    );
  }

  fetchShopifyLinks() {
    this.subs.sink = this.shopifyMigrationService.getShopifyLinks().pipe(
      catchError(() => {
        this.snackBar.open('Can\'t fetch shopify links.', 'DISMISS');
        return EMPTY;
      })
    ).subscribe(links => {
      this.shopifyLinksSubject$.next(links);
    });
  }

  isAppTransferedToShopify(featureLink: FeatureLink) {
    return this.shopifyLinks$.pipe(
      map(links => links.map(link => link.path)),
      map(paths => paths.includes(featureLink.path))
    );
  }

  isIconInFavorite(featureLink: FeatureLink) {
    return this.navigationService.getFavoritesSnapshot().includes(featureLink.path);
  }

  resolveFavorite(featureLink: FeatureLink, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if(this.isIconInFavorite(featureLink)) {
      this.subs.sink = this.navigationService.removeFavorite(featureLink.path).subscribe();
    } else{
      this.subs.sink = this.navigationService.addFavorite(featureLink.path).subscribe();
    }
  }

  openInShopify(featureLink: FeatureLink, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.shopifyLinks$.pipe(
      map(links => links.find(link => link.path === featureLink.path))
    ).subscribe(link => {
      if (link) {
        const win = window.open(link.shopifyLink, '_blank');
        win.focus();
      }
    });
  }

  async openSettings(featureLink: FeatureLink, shopifyLinks: ShopifyLink[], event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.subs.sink = this.dialog.open(EditShopifyLinkModalComponent, {
      width: '700px',
      disableClose: true,
      data: {
        featureLink: featureLink,
        shopifyLink: shopifyLinks.find(link => link.path === featureLink.path) || {
          forceRedirect: false,
          path: featureLink.path,
          shopifyLink: ''
        }
      }
    })
      .afterClosed()
      .subscribe((result: ShopifyLink) => {
        if (!result) {
          return;
        }

        this.shopifyMigrationService.updateShopifyLink(result).pipe(
          catchError(() => {
            this.snackBar.open('Can\'t update shopify link.', 'DISMISS');
            return EMPTY;
          }))
          .subscribe(() => {
            this.snackBar.open('Shopify link updated', 'DISMISS');
            this.fetchShopifyLinks();
          });
      });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
